import type { CurriculumPack } from './schema';

export interface ContentIssue {
  path: string;
  message: string;
}

/**
 * Cross-reference checks that a schema alone cannot express: every reference
 * points to something that exists, prerequisites have no cycles, answers are
 * among the options, and so on. Run in tests and when a pack is loaded.
 */
export function checkPackIntegrity(pack: CurriculumPack): ContentIssue[] {
  const issues: ContentIssue[] = [];
  const add = (path: string, message: string) => issues.push({ path, message });

  const unique = (kind: string, ids: string[]) => {
    const seen = new Set<string>();
    for (const id of ids) {
      if (seen.has(id)) add(`${kind}:${id}`, 'duplicate id');
      seen.add(id);
    }
    return seen;
  };

  const sourceIds = unique(
    'source',
    pack.sources.map((s) => s.id),
  );
  const subjectIds = unique(
    'subject',
    pack.subjects.map((s) => s.id),
  );
  const domainIds = unique(
    'domain',
    pack.domains.map((d) => d.id),
  );
  const objectiveIds = unique(
    'objective',
    pack.objectives.map((o) => o.id),
  );
  const variantIds = unique(
    'variant',
    pack.variants.map((v) => v.id),
  );
  unique(
    'item',
    pack.items.map((i) => i.id),
  );
  unique(
    'lesson',
    pack.lessons.map((l) => l.id),
  );
  unique(
    'level',
    pack.levels.map((l) => l.id),
  );

  const subjects = new Map(pack.subjects.map((s) => [s.id, s]));
  const domains = new Map(pack.domains.map((d) => [d.id, d]));
  const objectives = new Map(pack.objectives.map((o) => [o.id, o]));

  const checkSources = (path: string, refs: Array<{ sourceId: string }>) => {
    for (const ref of refs)
      if (!sourceIds.has(ref.sourceId)) add(path, `unknown source "${ref.sourceId}"`);
  };

  for (const subject of pack.subjects) checkSources(`subject:${subject.id}`, subject.sources);

  for (const domain of pack.domains) {
    if (!subjectIds.has(domain.subjectId))
      add(`domain:${domain.id}`, `unknown subject "${domain.subjectId}"`);
  }

  for (const objective of pack.objectives) {
    const path = `objective:${objective.id}`;
    const subject = subjects.get(objective.subjectId);
    if (!subject) add(path, `unknown subject "${objective.subjectId}"`);
    const domain = domains.get(objective.domainId);
    if (!domain || !domainIds.has(objective.domainId))
      add(path, `unknown domain "${objective.domainId}"`);
    else if (domain.subjectId !== objective.subjectId)
      add(path, 'domain belongs to another subject');
    if (subject) {
      for (const level of objective.levels) {
        if (!subject.levels.includes(level))
          add(path, `level "${level}" is not offered for subject "${subject.id}"`);
      }
      if (subject.teachingLanguage !== objective.teachingLanguage)
        add(path, 'teaching language differs from its subject');
    }
    for (const prerequisite of objective.prerequisites) {
      if (!objectiveIds.has(prerequisite)) add(path, `unknown prerequisite "${prerequisite}"`);
      if (prerequisite === objective.id) add(path, 'objective is its own prerequisite');
    }
    for (const variant of objective.variants ?? []) {
      if (!variantIds.has(variant)) add(path, `unknown variant "${variant}"`);
    }
    checkSources(path, objective.sources);
    if (objective.practice.type === 'bank') {
      const count = pack.items.filter((i) => i.objectiveId === objective.id).length;
      if (count < 3) add(path, `bank practice needs at least 3 items, found ${count}`);
    }
    if (
      objective.practice.type === 'counting' &&
      objective.practice.params.min >= objective.practice.params.max
    ) {
      add(path, 'counting range is empty');
    }
  }

  // Prerequisite cycles (depth-first search).
  const state = new Map<string, 'visiting' | 'done'>();
  const visit = (id: string, trail: string[]) => {
    if (state.get(id) === 'done') return;
    if (state.get(id) === 'visiting') {
      add(`objective:${id}`, `prerequisite cycle: ${[...trail, id].join(' → ')}`);
      return;
    }
    state.set(id, 'visiting');
    for (const next of objectives.get(id)?.prerequisites ?? []) visit(next, [...trail, id]);
    state.set(id, 'done');
  };
  for (const id of objectiveIds) visit(id, []);

  for (const lesson of pack.lessons) {
    if (!objectiveIds.has(lesson.objectiveId))
      add(`lesson:${lesson.id}`, `unknown objective "${lesson.objectiveId}"`);
  }

  for (const item of pack.items) {
    const path = `item:${item.id}`;
    const objective = objectives.get(item.objectiveId);
    if (!objective) {
      add(path, `unknown objective "${item.objectiveId}"`);
      continue;
    }
    if (objective.practice.type !== 'bank') add(path, 'objective does not use the item bank');
    switch (item.kind) {
      case 'choice': {
        const ids = item.options.map((o) => o.id);
        if (new Set(ids).size !== ids.length) add(path, 'duplicate option ids');
        const texts = item.options.map((o) => o.text);
        if (new Set(texts).size !== texts.length) add(path, 'duplicate option texts');
        if (!ids.includes(item.answer)) add(path, `answer "${item.answer}" is not an option`);
        break;
      }
      case 'order': {
        for (const alternative of item.alternatives ?? []) {
          if ([...alternative].sort().join('|') !== [...item.tokens].sort().join('|')) {
            add(path, 'alternative order uses different tokens');
          }
        }
        if (new Set(item.tokens).size === 1) add(path, 'all tokens are identical');
        break;
      }
      case 'match': {
        const lefts = item.pairs.map((p) => p.left);
        const rights = item.pairs.map((p) => p.right);
        if (new Set(lefts).size !== lefts.length || new Set(rights).size !== rights.length) {
          add(path, 'match pairs must have unique sides');
        }
        break;
      }
      case 'categorize': {
        const categories = new Set(item.categories.map((c) => c.id));
        for (const entry of item.entries) {
          if (!categories.has(entry.category))
            add(path, `entry "${entry.text}" uses unknown category "${entry.category}"`);
        }
        for (const category of categories) {
          if (!item.entries.some((e) => e.category === category))
            add(path, `category "${category}" has no entry`);
        }
        break;
      }
      default:
        break;
    }
  }

  return issues;
}
